import { LuExternalLink, LuPackage } from "react-icons/lu";
import { formatNumber } from "../../utils/format";
import { formatTaka } from "../Order/orderUtils";
import styles from "../../styles/cartPanel.module.css";

// What a customer has saved in the cart: every product with its image, the variations (size / colour ...) with unit
// price x quantity, the product total and the note, and the totals of the whole cart. Used under a row of the
// Customer Carts list and on the cart details page. `row` = a cart of GET carts / carts/:id ({ items, itemCount,
// totalQuantity, cartTotal }).
const variantText = (variation) => {
  const parts = Object.entries(variation.variant || {}).map(([name, value]) => `${name}: ${value}`);
  return parts.length ? parts.join(" / ") : variation.skuId;
};

const CartItemsPanel = ({ row }) => {
  const items = row.items || [];

  if (items.length === 0) return <p className={styles.empty}>This cart has no products.</p>;

  return (
    <div className={styles.panel}>
      <div className={styles.summary}>
        <span>
          <strong>{formatNumber(row.itemCount ?? items.length)}</strong> products
        </span>
        <span>
          <strong>{formatNumber(row.totalQuantity)}</strong> pieces
        </span>
        <span>
          Cart total <strong>{formatTaka(row.cartTotal)}</strong>
        </span>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Product</th>
              <th>Variations (unit price x quantity)</th>
              <th className={styles.num}>Qty</th>
              <th className={styles.num}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id ?? item.productCode}>
                <td>
                  <div className={styles.product}>
                    {item.image ? <img src={item.image} alt="" className={styles.image} loading="lazy" /> : <span className={`${styles.image} ${styles.noImage}`}><LuPackage /></span>}
                    <div className={styles.productInfo}>
                      <span className={styles.title} title={item.title}>
                        {item.title || "Untitled product"}
                      </span>
                      <span className={styles.meta}>
                        {item.productCode}
                        {item.source && <span className={styles.badge}>{item.source}</span>}
                        {item.sourceLink && (
                          <a href={item.sourceLink} target="_blank" rel="noopener noreferrer" className={styles.link} title="Open the product page">
                            <LuExternalLink />
                          </a>
                        )}
                      </span>
                      {item.note && <span className={styles.note}>Note: {item.note}</span>}
                    </div>
                  </div>
                </td>
                <td>
                  <ul className={styles.variations}>
                    {(item.variations || []).map((variation, index) => (
                      <li key={`${variation.skuId}-${index}`}>
                        <span className={styles.variant}>{variantText(variation)}</span>
                        <span className={styles.price}>
                          {formatTaka(variation.unitPrice)} x {formatNumber(variation.quantity)} = <strong>{formatTaka(variation.total)}</strong>
                        </span>
                      </li>
                    ))}
                  </ul>
                </td>
                <td className={styles.num}>{formatNumber(item.quantity)}</td>
                <td className={styles.num}>
                  <strong>{formatTaka(item.total)}</strong>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>Cart total</td>
              <td className={styles.num}>{formatNumber(row.totalQuantity)}</td>
              <td className={styles.num}>{formatTaka(row.cartTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default CartItemsPanel;
